import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {
    archiveRentalAsset,
    createRentalAsset,
    getRentalAsset,
    listRentalAssets,
    unarchiveRentalAsset,
    updateRentalAsset,
} from './api'
import {rentalAssetKeys} from './query-keys'
import type {CreateRentalAssetInput, UpdateRentalAssetInput} from './schemas'

type RentalAssetListFilters = {
    rentalAssetTypeId?: string
    includeArchived?: boolean
    enabled?: boolean
}

export function useRentalAssets(filters?: RentalAssetListFilters) {
    const {rentalAssetTypeId, includeArchived, enabled = true} = filters ?? {}
    return useQuery({
        queryKey: rentalAssetKeys.assets({rentalAssetTypeId, includeArchived}),
        queryFn: () => listRentalAssets({rentalAssetTypeId, includeArchived}),
        enabled,
    })
}

export function useRentalAsset(id: string) {
    return useQuery({
        queryKey: rentalAssetKeys.asset(id),
        queryFn: () => getRentalAsset(id),
    })
}

export function useCreateRentalAsset() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateRentalAssetInput) => createRentalAsset(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: ['rental-assets']})
        },
    })
}

export function useUpdateRentalAsset() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({id, input}: {id: string; input: UpdateRentalAssetInput}) =>
            updateRentalAsset(id, input),
        onSuccess: (_data, {id}) => {
            void queryClient.invalidateQueries({queryKey: ['rental-assets']})
            void queryClient.invalidateQueries({queryKey: rentalAssetKeys.asset(id)})
        },
    })
}

export function useArchiveRentalAsset() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => archiveRentalAsset(id),
        onSuccess: (_data, id) => {
            void queryClient.invalidateQueries({queryKey: ['rental-assets']})
            void queryClient.invalidateQueries({queryKey: rentalAssetKeys.asset(id)})
        },
    })
}

export function useUnarchiveRentalAsset() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => unarchiveRentalAsset(id),
        onSuccess: (_data, id) => {
            void queryClient.invalidateQueries({queryKey: ['rental-assets']})
            void queryClient.invalidateQueries({queryKey: rentalAssetKeys.asset(id)})
        },
    })
}
